import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Fab,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Publish,
  Schedule,
  Visibility,
  ContentCopy,
  MoreVert,
  Refresh,
  FilterList,
  ArrowBack,
  HourglassEmpty,
  AutoAwesome
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { Post, PostStatus, GenerationJobItem, GenerationJobStatus } from '../types/api';
import postsAPI from '../services/postsAPI';
import contentAPI from '../services/contentAPI';
import PostStatusBadge from '../components/Posts/PostStatusBadge';
import {
  countPostsByStatus,
  getPostStatusColor,
  POST_STATUS_LABELS,
} from '../utils/postStatus';
import { formatTimeInUserTimezone, formatTimeWithTimezone } from '../utils/timezone';

const PostsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // State management
  const [allPosts, setAllPosts] = useState<Post[]>([]); // Store all posts
  const [displayedPosts, setDisplayedPosts] = useState<Post[]>([]); // Posts to display on current page
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PostStatus | 'ALL'>('ALL');
  const [totalPosts, setTotalPosts] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<PostStatus, number>>({
    DRAFT: 0,
    SCHEDULED: 0,
    PUBLISHED: 0,
    PARTIAL: 0,
    FAILED: 0,
  });
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPost, setMenuPost] = useState<Post | null>(null);
  
  // Action loading states
  const [actionLoading, setActionLoading] = useState(false);

  // Generation jobs (async AI queue)
  const [generationJobs, setGenerationJobs] = useState<GenerationJobItem[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const hasActiveGenerationJobs = generationJobs.some(
    (job) => job.status === 'PENDING' || job.status === 'STARTED'
  );

  const fetchGenerationJobs = async () => {
    setJobsLoading(true);
    try {
      const response = await contentAPI.getGenerationJobs({ limit: 20 });
      const nextJobs = response.jobs || [];

      setGenerationJobs((prevJobs) => {
        const prevActive = prevJobs.filter(
          (j) => j.status === 'PENDING' || j.status === 'STARTED'
        );
        const newlyCompleted = nextJobs.filter(
          (job) =>
            job.status === 'SUCCESS' &&
            job.post_id &&
            prevActive.some((p) => p.id === job.id)
        );
        if (newlyCompleted.length > 0) {
          fetchPosts();
          toast.success(
            newlyCompleted.length === 1
              ? `Draft ready: "${newlyCompleted[0].topic}"`
              : `${newlyCompleted.length} drafts are ready to review`
          );
        }
        return nextJobs;
      });
    } catch (err) {
      console.error('Failed to fetch generation jobs:', err);
    } finally {
      setJobsLoading(false);
    }
  };

  // Fetch posts on component mount and when status filter changes
  useEffect(() => {
    fetchPosts();
    fetchGenerationJobs();
  }, [statusFilter]);

  // Poll generation jobs while any are in flight
  useEffect(() => {
    if (!hasActiveGenerationJobs) return undefined;

    const interval = setInterval(() => {
      fetchGenerationJobs();
    }, 5000);

    return () => clearInterval(interval);
  }, [hasActiveGenerationJobs]);

  // Handle pagination and filtering on client side
  useEffect(() => {
    applyFiltersAndPagination();
  }, [allPosts, page, rowsPerPage, searchTerm, statusFilter]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter]);

  const fetchStatusCounts = async () => {
    try {
      const response = await postsAPI.getPosts({ limit: 500 });
      setStatusCounts(countPostsByStatus(response.data || []));
    } catch (err) {
      console.error('Failed to fetch status counts:', err);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await postsAPI.getPosts({
        limit: 500,
        ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
        include_scheduling_info: statusFilter === 'SCHEDULED' || statusFilter === 'ALL',
      });

      setAllPosts(response.data || []);
      setTotalPosts(response.total || response.data?.length || 0);
      if (statusFilter === 'ALL') {
        setStatusCounts(countPostsByStatus(response.data || []));
      }
    } catch (err: any) {
      console.error('Failed to fetch posts:', err);
      setError('Failed to load posts. Please try again.');
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndPagination = () => {
    let filtered = allPosts.filter((post) => {
      const matchesSearch = post.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           post.content.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'ALL' || post.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    // Update total count based on filtered results
    setTotalPosts(filtered.length);

    // Apply pagination
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedPosts = filtered.slice(startIndex, endIndex);
    
    setDisplayedPosts(paginatedPosts);
  };

  const getStatusColor = getPostStatusColor;

  const statusFilterOptions: Array<PostStatus | 'ALL'> = [
    'ALL',
    'DRAFT',
    'SCHEDULED',
    'PUBLISHED',
    'PARTIAL',
    'FAILED',
  ];

  const getJobStatusColor = (
    status: GenerationJobStatus
  ): 'success' | 'warning' | 'info' | 'error' | 'default' => {
    switch (status) {
      case 'SUCCESS':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'STARTED':
        return 'info';
      case 'FAILURE':
        return 'error';
      default:
        return 'default';
    }
  };

  const getJobStatusLabel = (status: GenerationJobStatus): string => {
    switch (status) {
      case 'PENDING':
        return 'Queued';
      case 'STARTED':
        return 'Generating';
      case 'SUCCESS':
        return 'Complete';
      case 'FAILURE':
        return 'Failed';
      default:
        return status;
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, post: Post) => {
    setAnchorEl(event.currentTarget);
    setMenuPost(post);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPost(null);
  };

  const handleView = (post: Post) => {
    setSelectedPost(post);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleEdit = (post: Post) => {
    navigate(`/posts/edit/${post.id}`);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedPost) return;
    
    setActionLoading(true);
    try {
      await postsAPI.deletePost(selectedPost.id);
      toast.success('Post deleted successfully!');
      setDeleteDialogOpen(false);
      setSelectedPost(null);
      fetchPosts(); // Refresh the list
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error('Failed to delete post. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedPost) return;
    
    setActionLoading(true);
    try {
      await postsAPI.publishPost(selectedPost.id);
      toast.success('Post published successfully!');
      setPublishDialogOpen(false);
      setSelectedPost(null);
      fetchPosts(); // Refresh the list
    } catch (error: any) {
      console.error('Publish error:', error);
      toast.error('Failed to publish post. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDuplicate = async (post: Post) => {
    setActionLoading(true);
    try {
      await postsAPI.duplicatePost(post.id);
      toast.success('Post duplicated successfully!');
      fetchPosts(); // Refresh the list
    } catch (error: any) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate post. Please try again.');
    } finally {
      setActionLoading(false);
    }
    handleMenuClose();
  };

  // Helper function to create test posts for demonstration
  const createTestPosts = async () => {
    setActionLoading(true);
    try {
      const testPosts = [
        { topic: "AI Revolution in Healthcare", content: "Exploring how artificial intelligence is transforming medical diagnosis and treatment. From image recognition to predictive analytics, AI is revolutionizing patient care. #AIHealthcare #Innovation #MedTech", status: "PUBLISHED" as PostStatus },
        { topic: "Remote Work Best Practices", content: "Essential tips for maximizing productivity while working from home. Communication, time management, and work-life balance strategies. #RemoteWork #Productivity #WorkFromHome", status: "DRAFT" as PostStatus },
        { topic: "Sustainable Technology Trends", content: "Green tech innovations leading the charge toward a more sustainable future. Solar, wind, and emerging clean energy solutions. #GreenTech #Sustainability #Innovation", status: "SCHEDULED" as PostStatus },
        { topic: "Cybersecurity in 2024", content: "Latest threats and protection strategies for businesses and individuals. Zero-trust architecture and advanced threat detection. #Cybersecurity #InfoSec #TechSafety", status: "DRAFT" as PostStatus },
        { topic: "The Future of Electric Vehicles", content: "How EVs are reshaping transportation and infrastructure. Battery technology advances and charging network expansion. #ElectricVehicles #CleanEnergy #Transportation", status: "PUBLISHED" as PostStatus },
        { topic: "Data Privacy Regulations", content: "Understanding GDPR, CCPA, and emerging privacy laws. How businesses can ensure compliance while maintaining innovation. #DataPrivacy #Compliance #GDPR", status: "DRAFT" as PostStatus },
        { topic: "Machine Learning Applications", content: "Real-world ML use cases across industries. From recommendation systems to predictive maintenance. #MachineLearning #AI #TechApplications", status: "PUBLISHED" as PostStatus },
        { topic: "Cloud Computing Evolution", content: "Multi-cloud strategies and edge computing trends. How cloud infrastructure is enabling digital transformation. #CloudComputing #DigitalTransformation #Technology", status: "DRAFT" as PostStatus },
        { topic: "Blockchain Beyond Cryptocurrency", content: "Practical blockchain applications in supply chain, healthcare, and identity verification. #Blockchain #Technology #Innovation", status: "SCHEDULED" as PostStatus },
        { topic: "5G Technology Impact", content: "How 5G is enabling IoT, autonomous vehicles, and smart cities. The infrastructure revolution. #5G #IoT #SmartCities #Technology", status: "DRAFT" as PostStatus }
      ];

      for (let i = 0; i < testPosts.length; i++) {
        await postsAPI.createPost({
          topic: testPosts[i].topic,
          content: testPosts[i].content,
          platforms: ['linkedin'],
          status: testPosts[i].status,
          ...(testPosts[i].status === 'SCHEDULED' && {
            scheduled_time: new Date(Date.now() + (i + 1) * 24 * 60 * 60 * 1000).toISOString()
          })
        });
      }
      
      toast.success(`Created ${testPosts.length} test posts!`);
      fetchPosts(); // Refresh the list
    } catch (error: any) {
      console.error('Error creating test posts:', error);
      toast.error('Failed to create test posts');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/dashboard')}
              sx={{ mr: 2 }}
            >
              Back to Dashboard
            </Button>
            <Typography variant="h4" component="h1">
              Posts Management
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                fetchPosts();
                fetchGenerationJobs();
              }}
              disabled={loading || jobsLoading}
            >
              Refresh
            </Button>
            {totalPosts < 5 && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={createTestPosts}
                disabled={actionLoading}
                size="small"
              >
                {actionLoading ? 'Creating...' : 'Add Test Posts'}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/create-post')}
            >
              Create Post
            </Button>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Manage all your posts - view, edit, publish, schedule, and delete content
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Generation queue */}
      {generationJobs.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesome color="primary" />
              <Typography variant="h6">Generation queue</Typography>
              {hasActiveGenerationJobs && (
                <Chip
                  size="small"
                  color="info"
                  label="Live"
                  icon={<CircularProgress size={12} color="inherit" />}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {generationJobs.filter((j) => j.status === 'PENDING' || j.status === 'STARTED').length} in progress
            </Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Topic</TableCell>
                  <TableCell>Platforms</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Queued</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generationJobs.map((job) => (
                  <TableRow key={job.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {job.topic}
                      </Typography>
                      {job.variants > 1 && (
                        <Typography variant="caption" color="text.secondary">
                          {job.variants} variants
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(job.platforms?.length ? job.platforms : ['linkedin']).map((p) => (
                          <Chip key={p} label={p} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getJobStatusLabel(job.status)}
                        color={getJobStatusColor(job.status)}
                        size="small"
                        icon={
                          job.status === 'PENDING' ? (
                            <HourglassEmpty />
                          ) : job.status === 'STARTED' ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : undefined
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {job.message || '-'}
                      </Typography>
                      {job.error && (
                        <Typography variant="caption" color="error">
                          {job.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTimeInUserTimezone(job.created_at, 'Asia/Kolkata', 'MMM D, h:mm A')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {job.post_id ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Edit />}
                          onClick={() => navigate(`/posts/edit/${job.post_id}`)}
                        >
                          Open draft
                        </Button>
                      ) : job.status === 'FAILURE' ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => navigate('/create-post')}
                        >
                          Retry
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Status overview */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterList fontSize="small" color="action" />
          {statusFilterOptions.map((key) => {
            if (key === 'ALL') {
              const total = Object.values(statusCounts).reduce((a, b) => a + b, 0);
              return (
                <Chip
                  key="ALL"
                  label={`All (${total})`}
                  variant={statusFilter === 'ALL' ? 'filled' : 'outlined'}
                  color={statusFilter === 'ALL' ? 'primary' : 'default'}
                  onClick={() => setStatusFilter('ALL')}
                  size="small"
                />
              );
            }
            const count = statusCounts[key];
            return (
              <Chip
                key={key}
                label={`${POST_STATUS_LABELS[key]} (${count})`}
                variant={statusFilter === key ? 'filled' : 'outlined'}
                color={statusFilter === key ? getPostStatusColor(key) : 'default'}
                onClick={() => setStatusFilter(key)}
                size="small"
              />
            );
          })}
        </Box>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters & Search
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search posts by topic or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value as PostStatus | 'ALL')}
            >
              <MenuItem value="ALL">All Posts</MenuItem>
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              <MenuItem value="PUBLISHED">Published</MenuItem>
              <MenuItem value="PARTIAL">Partial</MenuItem>
              <MenuItem value="FAILED">Failed</MenuItem>
            </Select>
          </FormControl>
          
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Total: {totalPosts} posts
            {totalPosts <= rowsPerPage && totalPosts > 0 && (
              <Typography variant="caption" display="block" sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                All posts shown on one page
              </Typography>
            )}
          </Typography>
        </Box>
      </Paper>

      {/* Posts Table */}
      <Paper sx={{ mb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : displayedPosts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No posts found
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {searchTerm || statusFilter !== 'ALL' 
                ? 'Try adjusting your filters or search terms'
                : 'Start by creating your first post!'
              }
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/create-post')}
              sx={{ mt: 2 }}
            >
              Create Your First Post
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Topic</TableCell>
                  <TableCell>Content Preview</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Scheduled/Published</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedPosts.map((post) => (
                  <TableRow key={post.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {post.topic}
                      </Typography>
                      {post.image_url && (
                        <Chip label="Has Image" size="small" variant="outlined" sx={{ mt: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {post.content}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <PostStatusBadge post={post} />
                      {post.platforms && post.platforms.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {post.platforms.map((p) => (
                            <Chip key={p} label={p} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTimeInUserTimezone(post.created_at, 'Asia/Kolkata', 'MMM D, YYYY')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimeInUserTimezone(post.created_at, 'Asia/Kolkata', 'h:mm A')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {post.scheduled_time ? (
                        <Box>
                          <Typography variant="body2" color="warning.main">
                            Scheduled:
                          </Typography>
                          <Typography variant="caption">
                            {formatTimeWithTimezone(post.scheduled_time, 'Asia/Kolkata')}
                          </Typography>
                        </Box>
                      ) : post.published_time ? (
                        <Box>
                          <Typography variant="body2" color="success.main">
                            Published:
                          </Typography>
                          <Typography variant="caption">
                            {formatTimeWithTimezone(post.published_time, 'Asia/Kolkata')}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, post)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            <TablePagination
              component="div"
              count={totalPosts}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleView(menuPost!)}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleEdit(menuPost!)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        
        {menuPost?.status === 'DRAFT' && (
          <MenuItem onClick={() => {
            setSelectedPost(menuPost);
            setPublishDialogOpen(true);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <Publish fontSize="small" />
            </ListItemIcon>
            <ListItemText>Publish Now</ListItemText>
          </MenuItem>
        )}
        
        <MenuItem onClick={() => handleDuplicate(menuPost!)}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => {
          setSelectedPost(menuPost);
          setDeleteDialogOpen(true);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Post Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Post Details
        </DialogTitle>
        <DialogContent>
          {selectedPost && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedPost.topic}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <PostStatusBadge post={selectedPost} />
              </Box>
              
              <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedPost.content}
              </Typography>
              
              {selectedPost.image_url && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Attached Image:
                  </Typography>
                  <img
                    src={selectedPost.image_url}
                    alt="Post content"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 8
                    }}
                  />
                  {selectedPost.image_credit && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {selectedPost.image_credit}
                    </Typography>
                  )}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                <Typography variant="body2">
                  <strong>Created:</strong> {formatTimeWithTimezone(selectedPost.created_at, 'Asia/Kolkata')}
                </Typography>
                {selectedPost.scheduled_time && (
                  <Typography variant="body2">
                    <strong>Scheduled:</strong> {formatTimeWithTimezone(selectedPost.scheduled_time, 'Asia/Kolkata')}
                  </Typography>
                )}
                {selectedPost.published_time && (
                  <Typography variant="body2">
                    <strong>Published:</strong> {formatTimeWithTimezone(selectedPost.published_time, 'Asia/Kolkata')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
          {selectedPost?.status === 'DRAFT' && (
            <Button 
              variant="contained"
              onClick={() => {
                setViewDialogOpen(false);
                setPublishDialogOpen(true);
              }}
            >
              Publish Now
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedPost?.topic}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Delete />}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        aria-labelledby="publish-dialog-title"
      >
        <DialogTitle id="publish-dialog-title">
          Confirm Publish
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to publish "{selectedPost?.topic}" now? This action will make the post live immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPublishDialogOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePublish}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Publish />}
          >
            {actionLoading ? 'Publishing...' : 'Publish Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Tooltip title="Create New Post">
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={() => navigate('/create-post')}
        >
          <Add />
        </Fab>
      </Tooltip>
    </Container>
  );
};

export default PostsPage; 